import { useEffect, useCallback, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { OnboardingLayout } from './components'
import {
  BusinessTypeStep,
  PlaceSearchStep,
  PlaceConfirmStep,
  BusinessDetailsStep,
  EmailStep,
  NameStep,
  PhoneStep,
  LanguagesStep,
  BookingSystemStep,
  PaymentStep,
  ConfirmationStep,
} from './steps'
import { api } from '@/lib/api'
import { useOnboardingStore, inferStepFromData } from '@/stores/onboardingStore'
import { isValidStepKey, getStepIndex, getNextStep, getPrevStep, STEP, ONBOARDING_STEPS } from '@/types/onboarding'
import type { StepKey } from '@/types/onboarding'
import {
  useStartLeadOnboarding,
  useSaveBusinessType,
  useSavePlaceDetails,
  useSaveEmail,
  useSaveContact,
  useSaveLanguages,
  useLeadStatus,
  useLeadBookingDetection,
  useCreatePaymentContext,
} from '@/hooks/api/useLeadOnboarding'
import { useTurnstile } from '@/hooks/useTurnstile'

export function SignupPage() {
  const navigate = useNavigate()
  const { stepKey } = useParams<{ stepKey?: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const resumeLeadId = searchParams.get('lead_id')
  const assistantId = searchParams.get('assistant_id')

  const {
    leadId,
    currentStep,
    businessType,
    isManualEntry,
    bookingDetectionStarted,
    bookingDetection,
    setLeadId,
    setCurrentStep,
    setBookingDetection,
    setStripeClientSecret,
    setEmail,
    setPlaceDetails,
    getStepData,
    hydrateFromLead,
    reset,
    isStepComplete,
  } = useOnboardingStore()

  const startOnboarding = useStartLeadOnboarding()
  const createPaymentContext = useCreatePaymentContext()

  // Invisible Turnstile gate for the auto-firing /start call (junk signup vector).
  const { getToken: getStartTurnstileToken, reset: resetStartTurnstile } =
    useTurnstile()

  // Bounded auto-retry for /start. A 403 (Turnstile verification failed) would
  // otherwise dead-end the flow, since the init effect only re-runs on leadId/
  // resumeLeadId changes. Bumping this counter re-triggers the effect with a
  // fresh token; capped so a hard failure can't loop forever.
  const MAX_START_ATTEMPTS = 3
  const [startAttempt, setStartAttempt] = useState(0)

  // Domain-specific save mutations for steps handled by the orchestrator
  const saveBusinessType = useSaveBusinessType()
  const savePlaceDetails = useSavePlaceDetails()
  const saveEmail = useSaveEmail()
  const saveContact = useSaveContact()
  const saveLanguages = useSaveLanguages()

  // Step → mutation dispatch (booking/payment steps are handled directly by their components).
  const stepMutationsRef = useRef<Record<string, { mutateAsync: (args: { leadId: string; data: any }) => Promise<any> } | undefined>>({})
  stepMutationsRef.current = {
    [STEP.BUSINESS_TYPE]: saveBusinessType,
    [STEP.PLACE_CONFIRM]: savePlaceDetails,
    [STEP.EMAIL]: saveEmail,
    [STEP.NAME]: saveContact,
    [STEP.LANGUAGES]: saveLanguages,
  }

  // Fetch lead status from API to detect if onboarding is already complete
  const { data: leadData } = useLeadStatus(leadId, !!leadId)
  const isOnboardingComplete = !!leadData && leadData.status !== 'DRAFT'

  // Poll for booking detection after place details are saved
  const detectionCompleted = bookingDetection?.status === 'COMPLETED' || bookingDetection?.status === 'FAILED'
  const pollingEnabled = bookingDetectionStarted && !detectionCompleted && !!leadId

  const { data: detectionResult } = useLeadBookingDetection(leadId, pollingEnabled)

  // Update store when detection completes
  useEffect(() => {
    if (detectionResult && (detectionResult.status === 'COMPLETED' || detectionResult.status === 'FAILED')) {
      setBookingDetection(detectionResult)
    }
  }, [detectionResult, setBookingDetection])

  // Derive the requested step from the URL param
  const isInvalidStepKey = !!stepKey && !isValidStepKey(stepKey)
  const requestedStep: StepKey = (stepKey && isValidStepKey(stepKey)) ? stepKey : currentStep

  // Validate that we can access the requested step
  const canAccessStep = (step: StepKey): boolean => {
    if (isOnboardingComplete) return getStepIndex(step) >= getStepIndex(STEP.CONFIRMATION)
    if (step === STEP.BUSINESS_TYPE) return true
    if (getStepIndex(step) <= getStepIndex(currentStep)) return true
    const targetIdx = getStepIndex(step)
    for (let i = 0; i < targetIdx; i++) {
      if (!isStepComplete(ONBOARDING_STEPS[i].key)) return false
    }
    return true
  }

  // Determine active step — if lead is complete force confirmation, otherwise validate access
  const activeStep: StepKey = isOnboardingComplete
    ? STEP.CONFIRMATION
    : canAccessStep(requestedStep) ? requestedStep : currentStep

  // Assistant-level payment link: hydrate billing + jump straight to payment step
  const assistantAttempted = useRef<string | null>(null)
  useEffect(() => {
    if (!assistantId || assistantAttempted.current === assistantId) return
    assistantAttempted.current = assistantId

    createPaymentContext.mutateAsync({ assistant_id: parseInt(assistantId, 10) })
      .then((ctx) => {
        if (ctx.lead_id) setLeadId(ctx.lead_id)
        if (ctx.billing.email) setEmail(ctx.billing.email)
        if (ctx.billing.company_name) {
          setPlaceDetails({ name: ctx.billing.company_name, address: ctx.billing.address || '', phone: '', website: '', google_place_id: '', useSamePhoneForEvents: false })
        }
        if (ctx.client_secret) setStripeClientSecret(ctx.client_secret)
        navigate('/signup/payment', { replace: true })
      })
      .catch(() => navigate('/signup/payment', { replace: true }))
  }, [assistantId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Resume from URL lead_id or initialize a new onboarding session
  const resumeAttempted = useRef<string | null>(null)

  useEffect(() => {
    const initSession = async () => {
      // If a lead_id is in the URL, always hydrate from the API — the link
      // is the source of truth (e.g. abandonment email on same or other device)
      if (resumeLeadId && resumeAttempted.current !== resumeLeadId) {
        resumeAttempted.current = resumeLeadId
        try {
          const response = await api.get(`/public/onboarding/${resumeLeadId}`)
          hydrateFromLead(response.data)
          // Navigate to the inferred step — this must happen before cleaning
          // the lead_id param, otherwise the URL sync effect intervenes
          const step = inferStepFromData(response.data.data, response.data.phone_verified)
          navigate(`/signup/${step}`, { replace: true })
          return
        } catch {
          // Lead not found or not resumable — fall through to start fresh
        }
      }

      if (!leadId && !startOnboarding.isPending) {
        // Obtain a Turnstile token to accompany /start. If the widget can't
        // produce one (script blocked, etc.) we still attempt the call so the
        // flow isn't hard-blocked; the backend rejects with 403 once verification
        // is live, which we handle below.
        let turnstileToken: string | undefined
        try {
          turnstileToken = await getStartTurnstileToken()
        } catch {
          turnstileToken = undefined
        }

        try {
          const result = await startOnboarding.mutateAsync(turnstileToken)
          setLeadId(result.lead_id)
          setBookingDetection(null)
        } catch (error) {
          // A 403 means Turnstile verification failed — clear the token and,
          // within the retry cap, bump startAttempt to re-run this effect and
          // request a fresh token instead of dead-ending the flow.
          const status = (error as { response?: { status?: number } })?.response
            ?.status
          if (status === 403) {
            resetStartTurnstile()
            if (startAttempt < MAX_START_ATTEMPTS) {
              setStartAttempt((n) => n + 1)
            }
          }
          console.error('Failed to start onboarding:', error)
        }
      }
    }

    initSession()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, resumeLeadId, startAttempt])

  // Track programmatic navigation to prevent the sync effect from
  // reverting currentStep due to a stale URL during transitions.
  const pendingNavStep = useRef<StepKey | null>(null)

  // Sync step with URL and redirect if trying to access invalid step
  useEffect(() => {
    // Don't redirect while a resume lead_id is pending — the async resume
    // effect needs the query param intact before it can hydrate
    if (resumeLeadId && resumeAttempted.current !== resumeLeadId) return

    if (pendingNavStep.current !== null) {
      if (requestedStep === pendingNavStep.current) {
        pendingNavStep.current = null
      }
      return
    }
    if (isInvalidStepKey) {
      navigate('/404', { replace: true })
      return
    }
    // Redirect bare /signup or mismatched step to the correct path
    if (!stepKey || requestedStep !== activeStep) {
      navigate(`/signup/${activeStep}`, { replace: true })
    }
    if (activeStep !== currentStep) {
      setCurrentStep(activeStep)
    }
  }, [activeStep, currentStep, requestedStep, stepKey, setCurrentStep, navigate, resumeLeadId])

  // Navigate to a step — updates both store and URL path
  const goToStep = useCallback((step: StepKey) => {
    pendingNavStep.current = step
    setCurrentStep(step)
    navigate(`/signup/${step}`)
    window.scrollTo(0, 0)
  }, [navigate, setCurrentStep])

  // Save step data and proceed — dispatches to the correct domain endpoint
  const handleSaveAndNext = useCallback(async (step: StepKey) => {
    if (!leadId) return

    const mutation = stepMutationsRef.current[step]
    if (mutation) {
      const data = getStepData(step)
      try {
        await mutation.mutateAsync({ leadId, data })
      } catch (error) {
        console.error(`Failed to save step ${step}:`, error)
      }
    }
    let next = getNextStep(step)
    // Payment is decoupled from the signup flow — skip directly to confirmation.
    // Payment remains accessible via direct URL (e.g. /signup/payment?lead_id=xxx).
    if (next === STEP.PAYMENT) next = STEP.CONFIRMATION
    if (next) goToStep(next)
  }, [leadId, getStepData, goToStep])

  const handleNext = useCallback(() => {
    handleSaveAndNext(currentStep)
  }, [handleSaveAndNext, currentStep])

  const handleBack = useCallback(() => {
    if (isOnboardingComplete) return
    const prev = getPrevStep(currentStep)
    if (prev) goToStep(prev)
  }, [currentStep, goToStep, isOnboardingComplete])

  // Determine if hero panel should be shown (show for place selection steps)
  const showHeroPanel = getStepIndex(currentStep) <= getStepIndex(STEP.PLACE_CONFIRM)

  // Handle manual entry navigation (skip to place-confirm with manual entry mode)
  const handleManualEntry = useCallback(() => {
    goToStep(STEP.PLACE_CONFIRM)
  }, [goToStep])

  // Skip booking system step for non-restaurants
  const hasSkippedBookingStep = useRef(false)
  useEffect(() => {
    if (currentStep === STEP.BOOKING_SYSTEM && businessType !== 'RESTAURANT' && !hasSkippedBookingStep.current) {
      hasSkippedBookingStep.current = true
      goToStep(STEP.CONFIRMATION)
    }
    if (currentStep !== STEP.BOOKING_SYSTEM) {
      hasSkippedBookingStep.current = false
    }
  }, [currentStep, businessType, goToStep])

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case STEP.BUSINESS_TYPE:
        return <BusinessTypeStep onNext={handleNext} />
      case STEP.PLACE_SEARCH:
        return (
          <PlaceSearchStep
            onNext={handleNext}
            onBack={handleBack}
            onManualEntry={handleManualEntry}
          />
        )
      case STEP.PLACE_CONFIRM:
        if (isManualEntry) {
          return (
            <BusinessDetailsStep
              onNext={handleNext}
              onBack={handleBack}
            />
          )
        }
        return (
          <PlaceConfirmStep
            onNext={handleNext}
            onBack={handleBack}
          />
        )
      case STEP.EMAIL:
        return <EmailStep onNext={handleNext} onBack={handleBack} />
      case STEP.NAME:
        return <NameStep onNext={handleNext} onBack={handleBack} />
      case STEP.PHONE:
        return <PhoneStep onNext={handleNext} onBack={handleBack} />
      case STEP.LANGUAGES:
        return (
          <LanguagesStep
            onNext={handleNext}
            onBack={handleBack}
          />
        )
      case STEP.BOOKING_SYSTEM:
        if (businessType !== 'RESTAURANT') {
          return null
        }
        return (
          <BookingSystemStep
            onNext={handleNext}
            onBack={handleBack}
            onSkip={handleNext}
          />
        )
      case STEP.PAYMENT:
        return <PaymentStep onNext={handleNext} onBack={handleBack} />
      case STEP.CONFIRMATION:
        return <ConfirmationStep />
      default:
        return <BusinessTypeStep onNext={handleNext} />
    }
  }

  // Progress bar: 1-based index, excluding confirmation
  const totalSteps = getStepIndex(STEP.BOOKING_SYSTEM) + 1
  const progress = getStepIndex(currentStep) + 1

  const wideLayout = false
  const fullWidthContent = currentStep === STEP.PAYMENT || currentStep === STEP.CONFIRMATION
  const showSignIn = getStepIndex(currentStep) < getStepIndex(STEP.EMAIL)
  const hideProgressBar = currentStep === STEP.CONFIRMATION || currentStep === STEP.PAYMENT

  return (
    <OnboardingLayout
      showHeroPanel={showHeroPanel}
      showSignIn={showSignIn}
      progress={progress}
      totalSteps={totalSteps}
      wideLayout={wideLayout}
      fullWidthContent={fullWidthContent}
      hideProgressBar={hideProgressBar}
    >
      {renderStep()}
    </OnboardingLayout>
  )
}
